import { z } from 'zod';
import { ITEM_LEVEL_CAPS } from './definitions';
import { JEWEL_DEFINITIONS } from './jewels';

/**
 * O que as joias encaixadas de um casco SÃO, agregado, para atravessar o fio.
 *
 * ## O problema que este ficheiro resolve
 *
 * A ocupação real vive em `skin_socket_occupancy`, com UUIDs de skin, de socket
 * e de instância, ligada a `item_instances` por dono, estado e nível. Nada
 * disso pode entrar numa simulação: um duelo que carregasse um UUID de
 * instância teria, dentro do laço de combate, a chave com que se lê o
 * inventário - e a distância entre "tem a chave" e "usa a chave" é uma linha
 * que alguém escreve numa quinta-feira.
 *
 * Então a fronteira é aqui. O que atravessa é **quanta** Chama da Destruição
 * este casco tem e **que níveis somados**, e mais nada. Não há campo onde um
 * identificador de posse caiba, o que é uma garantia mais forte do que a
 * disciplina de não o preencher.
 *
 * ## `copies` é sempre **1**, e `levelSum` é o NÍVEL (gate V28G.K)
 *
 * A forma é a mesma - `{ id, copies, levelSum }` - e a semântica não é. Uma
 * skin nunca tem duas joias do mesmo tipo, portanto cada entrada descreve uma
 * peça só, `copies` é 1 por lei, e `levelSum` deixou de ser uma soma: é o nível
 * daquela joia, entre 1 e {@link JEWEL_LEVEL_MAX}.
 *
 * O nome ficou. Renomeá-lo obrigaria a subir a versão de todos os documentos
 * que o carregam - instantâneas, bilhetes, loadouts - para exprimir uma coisa
 * que a validação já diz melhor do que uma palavra diria. O campo continua a
 * significar "quantos níveis desta joia este casco tem", e com uma joia só isso
 * é o nível dela.
 *
 * O que DESAPARECEU como comportamento válido: seis cópias iguais,
 * `levelSum` 180, e a agregação que somava duas peças do mesmo tipo numa
 * entrada. Uma duplicata encontrada numa fronteira autoritativa é recusada, e
 * nunca fundida - fundir seria a fronteira a inventar um casco que a base não
 * tem.
 *
 * ## O que este ficheiro NÃO sabe
 *
 * Dano, cadência, vida, redução, procs, DoT, cura, empurrão, VFX, chance,
 * cooldown, custo, obtenção e mercado. As catorze concedem `effects: {}` e o
 * vazio é o estado real do conteúdo; um número escrito aqui seria uma decisão
 * de produto tomada por um ficheiro de transporte.
 */

/* -------------------------------------------------------------------------- */
/*  As catorze identidades                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Derivadas do catálogo, e a derivação é a garantia.
 *
 * Uma segunda lista escrita à mão concordaria consigo mesma para sempre,
 * incluindo no dia em que o catálogo mudasse - e uma quinzena joia de socket
 * entraria em produção sem que nada nesta fronteira reparasse. `socketMode` é o
 * campo que a ADR 0049 criou precisamente para esta pergunta ser respondida
 * pelo catálogo em vez de por uma comparação de id.
 *
 * Ordenadas, porque a ordem é o que dá ao perfil uma representação única.
 */
export const SOCKETED_JEWEL_IDS: readonly string[] = Object.freeze(
  JEWEL_DEFINITIONS.filter((jewel) => jewel.socketMode === 'socketed')
    .map((jewel) => jewel.definitionId)
    .sort(),
);

const SOCKETED = new Set(SOCKETED_JEWEL_IDS);

/**
 * Seis, que é o tecto de `skin_instances.socket_capacity`.
 *
 * A capacidade de uma cópia é 1..6 e imutável (ADR 0040, revista pela 0045),
 * por isso nenhuma skin pode ter sete assentos e nenhum perfil legal pode
 * descrever sete joias. É um limite de REPRESENTAÇÃO derivado de uma regra de
 * esquema - não um tecto sobre o que um item concede, que o `CLAUDE.md` proíbe.
 */
export const JEWEL_SOCKET_CAPACITY_MAX = 6;

/**
 * O nível máximo de uma joia: **30**, e é o teto final (gate V28G.K).
 *
 * LIDO do catálogo em vez de escrito, como já era quando o catálogo dizia
 * "nível 1 apenas". A migração 0051 põe o mesmo 30 em
 * `item_definitions.max_level`, e o gatilho de nível da 0003 recusa 0 e recusa
 * 31 na escrita - portanto este número e o da base não podem divergir sem que
 * uma das duas fronteiras grite.
 *
 * Nada cresce depois do 30: nem chance, nem dano, nem cura, nem cadência. As
 * funções puras de matemática têm um `min(nível, 30)` defensivo para o
 * provarem, e esse clamp NÃO autoriza dados corrompidos - armazenamento,
 * socket, API, bilhete, instantânea e perfil recusam 31 como inválido em vez de
 * o cortarem em silêncio.
 */
export const JEWEL_LEVEL_MAX = ITEM_LEVEL_CAPS.jewel ?? 1;

/* -------------------------------------------------------------------------- */
/*  O perfil                                                                  */
/* -------------------------------------------------------------------------- */

export interface JewelSocketProfileEntry {
  /** Uma das catorze. Nunca a peça soberana, que não ocupa socket. */
  readonly id: string;
  /**
   * **Sempre 1.** Uma skin nunca tem duas joias do mesmo tipo.
   *
   * O campo sobrevive à regra que o justificava porque removê-lo custaria uma
   * versão nova a cada documento que o carrega, e porque `1` num campo validado
   * é uma afirmação mais forte do que a ausência do campo.
   */
  readonly copies: 1;
  /** O NÍVEL desta joia, de 1 a {@link JEWEL_LEVEL_MAX}. */
  readonly levelSum: number;
}

export interface JewelSocketProfile {
  readonly entries: readonly JewelSocketProfileEntry[];
}

/**
 * Nenhuma joia, e é o que toda a conta tem hoje.
 *
 * Congelado, e partilhado em vez de reconstruído: um casco sem joias é o caso
 * comum em todas as partidas e em todas as caçadas deste gate, e um objeto novo
 * por participante seria alocação por cadeira para descrever a mesma ausência.
 */
export const EMPTY_JEWEL_SOCKET_PROFILE: JewelSocketProfile = Object.freeze({
  entries: Object.freeze([]) as readonly JewelSocketProfileEntry[],
});

export function jewelSocketProfileIsEmpty(profile: JewelSocketProfile): boolean {
  return profile.entries.length === 0;
}

/* -------------------------------------------------------------------------- */
/*  Validação                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Uma entrada, com as duas afirmações do V28G.K escritas como tipos.
 *
 * `z.literal(1)` em vez de um intervalo: `copies: 2` deixou de ser um valor
 * grande demais e passou a ser uma impossibilidade, e um decoder que a
 * aceitasse estaria a descrever uma skin que a base não consegue ter. É a mesma
 * recusa que o índice único dá, na outra ponta do fio.
 */
const entrySchema = z
  .object({
    id: z.string().refine((id) => SOCKETED.has(id), {
      message: 'not a socketed jewel of the canonical catalogue',
    }),
    copies: z.literal(1),
    levelSum: z.number().int().min(1).max(JEWEL_LEVEL_MAX),
  })
  .strict();

/**
 * O perfil inteiro, com as três regras que uma entrada sozinha não pode ter.
 *
 * `.strict()` nos dois níveis: uma chave desconhecida é recusada em vez de
 * ignorada, porque um campo que ninguém lê num formato assinado é um campo que
 * um dia alguém lê.
 */
export const jewelSocketProfileSchema = z
  .object({
    /*
     * `.readonly()` para o tipo INFERIDO ser `readonly`, e não só o da mão.
     *
     * Sem ele, `ResolvedLoadout` - que declara `JewelSocketProfile` - deixa de
     * ser atribuível a `ResolvedLoadoutWire`, que o zod infere mutável. As duas
     * descrevem o mesmo documento e têm de continuar a poder ser a mesma coisa.
     */
    entries: z.array(entrySchema).max(JEWEL_SOCKET_CAPACITY_MAX).readonly(),
  })
  .strict()
  .superRefine((profile, ctx) => {
    const ids = profile.entries.map((entry) => entry.id);
    if (new Set(ids).size !== ids.length) {
      ctx.addIssue({
        code: 'custom',
        message: 'duplicated definition: the profile is aggregated, one entry per jewel',
      });
    }
    if (ids.some((id, index) => index > 0 && id <= (ids[index - 1] ?? ''))) {
      ctx.addIssue({
        code: 'custom',
        message: 'entries out of order: a hull has exactly one representation',
      });
    }
    const total = profile.entries.length;
    if (total > JEWEL_SOCKET_CAPACITY_MAX) {
      ctx.addIssue({
        code: 'custom',
        message: `${total} jewels: a skin copy has at most ${JEWEL_SOCKET_CAPACITY_MAX} sockets`,
      });
    }
  });

/** Valida um perfil vindo de fora, atirando em vez de adivinhar. */
export function parseJewelSocketProfile(value: unknown): JewelSocketProfile {
  const parsed = jewelSocketProfileSchema.parse(value);
  return Object.freeze({
    entries: Object.freeze(parsed.entries.map((entry) => Object.freeze({ ...entry }))),
  });
}

/* -------------------------------------------------------------------------- */
/*  Agregação                                                                 */
/* -------------------------------------------------------------------------- */

/** Uma joia realmente encaixada, como o resolvedor a lê da base. */
export interface OccupiedJewel {
  readonly definitionId: string;
  readonly level: number;
}

/**
 * A ocupação real de uma skin, agregada em perfil.
 *
 * A ordem de entrada não importa e não pode importar: a base devolve linhas por
 * `socket_index`, e um perfil que dependesse dessa ordem daria dois cascos
 * diferentes para a mesma nave conforme a joia estivesse no socket 1 ou no 4.
 * O resultado sai ordenado por id, que é a única representação de um estado.
 *
 * Atira em vez de filtrar. Uma peça soberana, um id que não é joia, um nível
 * fora do contrato, uma sétima joia ou **duas do mesmo tipo** são
 * impossibilidades que a base já recusa - o gatilho da ocupação, o gatilho de
 * nível, a capacidade imutável e o índice
 * `skin_socket_occupancy_one_of_each_type_idx` - portanto encontrá-las aqui
 * significa que uma delas falhou, e continuar em silêncio seria construir um
 * casco a partir de um estado que não devia existir.
 *
 * A duplicata em particular é RECUSADA e nunca coalescida. Somar duas Chamas
 * numa entrada daria um casco jogável a partir de dados corrompidos, e o
 * jogador entraria numa partida com uma nave que a base não descreve.
 */
export function jewelSocketProfileFrom(occupied: readonly OccupiedJewel[]): JewelSocketProfile {
  if (occupied.length > JEWEL_SOCKET_CAPACITY_MAX) {
    throw new RangeError(
      `${occupied.length} jewels occupy sockets: a skin copy has at most ${JEWEL_SOCKET_CAPACITY_MAX}`,
    );
  }
  const byId = new Map<string, number>();
  for (const jewel of occupied) {
    if (!SOCKETED.has(jewel.definitionId)) {
      throw new RangeError(
        `${jewel.definitionId} does not occupy a socket: only a socketed jewel does, never a sovereign one`,
      );
    }
    if (!Number.isSafeInteger(jewel.level) || jewel.level < 1 || jewel.level > JEWEL_LEVEL_MAX) {
      throw new RangeError(
        `${jewel.definitionId} at level ${jewel.level}: the contract allows 1..${JEWEL_LEVEL_MAX}`,
      );
    }
    if (byId.has(jewel.definitionId)) {
      throw new RangeError(
        `${jewel.definitionId} occupies two sockets of the same skin: a skin holds one jewel of each type`,
      );
    }
    byId.set(jewel.definitionId, jewel.level);
  }
  return Object.freeze({
    entries: Object.freeze(
      [...byId.entries()]
        .sort(([left], [right]) => (left < right ? -1 : left > right ? 1 : 0))
        .map(([id, level]) => Object.freeze({ id, copies: 1 as const, levelSum: level })),
    ),
  });
}
